import React from 'react';
import { Box, Chip, LinearProgress, Tooltip, Typography } from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { TFunction } from 'i18next';
import type { DataTableColumn } from '../../../accounting/shared/AccountingDataTable';
import type { ProductionOrderListItem } from '../../types/production.types';
import {
  formatMoney,
  formatProductionDate,
  formatQuantity,
  formatWarehouses,
  shortCreatedBy,
} from '../../utils/productionFormat';
import ProductionStatusChip from '../shared/ProductionStatusChip';
import QualityStatusChip from '../shared/QualityStatusChip';

const MOBILE_COLUMN_IDS = new Set(['orderNumber', 'product', 'status', 'produced', 'steps']);
const COST_STATUSES = new Set(['COMPLETED', 'CANCELLED']);

/** Durum ve kalite etiketleri her satırda aynı genişlikte ve aynı x konumunda başlar. */
const STATUS_CHIP_WIDTH = 104;
const statusChipSx = { width: STATUS_CHIP_WIDTH, justifyContent: 'center' } as const;

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/** Uzun metin tek satırda kesilir, tamamı ipucunda görünür. */
function ClippedText({ text, maxWidth }: { text: string; maxWidth: number }) {
  return (
    <Tooltip title={text} placement="top-start">
      <Box component="span" sx={{ ...ellipsis, display: 'block', maxWidth }}>{text}</Box>
    </Tooltip>
  );
}

export function buildProductionOrderColumns(
  t: TFunction,
  hasBranches: boolean,
  isMobile: boolean,
  showSteps = false,
): DataTableColumn<ProductionOrderListItem>[] {
  const columns: DataTableColumn<ProductionOrderListItem>[] = [
    {
      id: 'orderNumber',
      label: t('orders.columns.orderNumber'),
      field: 'orderNumber',
      sortable: true,
      minWidth: '150px',
      // Fiş no kesilmez; hücre içeriği kadar genişler.
      cellSx: { overflow: 'visible', textOverflow: 'clip' },
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
          {row.level > 0 && (
            <Tooltip title={t('orders.parentOrder', { number: row.parentOrderNumber || '—' })}>
              <SubdirectoryArrowRightIcon fontSize="small" color="action" />
            </Tooltip>
          )}
          <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontSize: 'inherit' }}>{row.orderNumber}</Typography>
          {row.lotWarning ? (
            <Tooltip title={row.lotWarning}>
              <WarningAmberIcon fontSize="small" color="warning" />
            </Tooltip>
          ) : null}
          {row.childCount > 0 && (
            <Chip size="small" variant="outlined" label={t('orders.childCount', { count: row.childCount })} />
          )}
        </Box>
      ),
    },
    {
      id: 'productionDate',
      label: t('orders.columns.date'),
      field: 'productionDate',
      sortable: true,
      minWidth: '110px',
      render: (row) => formatProductionDate(row.productionDate),
    },
    {
      id: 'product',
      label: t('orders.columns.product'),
      field: 'productName',
      sortable: true,
      minWidth: '140px',
      render: (row) => (
        <Tooltip title={row.productName} placement="top-start">
          <Box sx={{ maxWidth: 200 }}>
            <Typography variant="body2" sx={{ ...ellipsis, fontSize: 'inherit' }}>{row.productName}</Typography>
            {row.stockCode ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ...ellipsis, fontSize: '0.7rem' }}>
                {row.stockCode}
              </Typography>
            ) : null}
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'planned',
      label: t('orders.columns.planned'),
      align: 'right',
      minWidth: '110px',
      render: (row) => formatQuantity(row.plannedQuantity, row.unit),
    },
    {
      id: 'produced',
      label: t('orders.columns.produced'),
      align: 'right',
      minWidth: '110px',
      render: (row) => (
        row.status === 'COMPLETED'
          ? formatQuantity(row.producedQuantity, row.unit)
          : '—'
      ),
    },
    {
      id: 'warehouses',
      label: t('orders.columns.warehouses'),
      minWidth: '130px',
      render: (row) => <ClippedText text={formatWarehouses(row.inputWarehouseName, row.outputWarehouseName)} maxWidth={200} />,
    },
    {
      id: 'status',
      label: t('orders.columns.status'),
      field: 'status',
      sortable: true,
      minWidth: `${STATUS_CHIP_WIDTH + 24}px`,
      // Fatura listesindeki Tür sütunu gibi: üstte çerçeveli durum, altta aynı genişlikte dolu kontrol sonucu.
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
          <ProductionStatusChip status={row.status} sx={statusChipSx} />
          <QualityStatusChip status={row.qualityStatus} sx={statusChipSx} />
        </Box>
      ),
    },
    ...(showSteps ? [{
      id: 'steps',
      label: t('orders.columns.steps'),
      minWidth: '110px',
      render: (row: ProductionOrderListItem) => {
        if (!row.hasRouting) return '-';
        const done = row.stepsDone ?? 0;
        const total = row.stepsTotal ?? 0;
        const value = total > 0 ? Math.min(100, (done / total) * 100) : 0;
        return (
          <Box sx={{ minWidth: 72 }}>
            <Typography variant="caption">{done}/{total}</Typography>
            <LinearProgress variant="determinate" value={value} sx={{ height: 4 }} />
          </Box>
        );
      },
    } satisfies DataTableColumn<ProductionOrderListItem>] : []),
    {
      id: 'actualCost',
      label: t('orders.columns.actualCost'),
      field: 'actualTotalCost',
      sortable: true,
      align: 'right',
      minWidth: '130px',
      render: (row) => (
        COST_STATUSES.has(row.status) ? formatMoney(row.actualTotalCost) : '—'
      ),
    },
    {
      id: 'unitCost',
      label: t('orders.columns.unitCost'),
      align: 'right',
      minWidth: '120px',
      render: (row) => (
        COST_STATUSES.has(row.status) ? formatMoney(row.actualUnitCost, 4) : '—'
      ),
    },
    {
      id: 'branch',
      label: t('orders.columns.branch'),
      minWidth: '90px',
      render: (row) => <ClippedText text={row.branchName?.trim() || '—'} maxWidth={140} />,
    },
    {
      id: 'createdBy',
      label: t('orders.columns.createdBy'),
      minWidth: '100px',
      render: (row) => <ClippedText text={shortCreatedBy(row.createdBy)} maxWidth={130} />,
    },
  ];

  const visible = hasBranches ? columns : columns.filter((column) => column.id !== 'branch');
  return isMobile ? visible.filter((column) => MOBILE_COLUMN_IDS.has(column.id)) : visible;
}
